import { useId } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import FormField from './FormField';
import styles from './Input.module.scss';

// ============================================
// TYPES
// ============================================

export type InputSize = 'sm' | 'md' | 'lg';

export interface TextInputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size'> {
  /** Forwarded ref to the underlying <input> element */
  ref?: React.Ref<HTMLInputElement>;
  /** Input size variant */
  size?: InputSize;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Decorative icon on the left side of the input */
  leftIcon?: ReactNode;
  /** Decorative icon on the right side (non-interactive) */
  rightIcon?: ReactNode;
  /** Interactive action element on the right (e.g. clear button, visibility toggle) */
  rightAction?: ReactNode;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
}

// ============================================
// COMPONENT
// ============================================

function TextInput({
  ref,
  size = 'md',
  label,
  isRequired = false,
  helperText,
  error,
  leftIcon,
  rightIcon,
  rightAction,
  fullWidth = true,
  className,
  id: idProp,
  disabled,
  type = 'text',
  ...inputProps
}: TextInputProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);
  const hasRight = Boolean(rightIcon) || Boolean(rightAction);

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = styles[size] ?? styles.md;

  const fieldClassName = [
    sizeClass,
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [
    styles.inputWrapper,
    leftIcon && styles.hasLeftIcon,
    hasRight && styles.hasRightIcon,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <div className={wrapperClasses}>
        {leftIcon && <span className={styles.inputIconLeft}>{leftIcon}</span>}

        <input
          ref={ref}
          id={id}
          type={type}
          disabled={disabled}
          className={styles.textInput}
          aria-invalid={hasError || undefined}
          aria-required={isRequired || undefined}
          aria-describedby={describedBy}
          {...(type === 'number' ? { onWheel: (e) => e.currentTarget.blur() } : {})}
          {...inputProps}
        />

        {rightIcon && <span className={styles.inputIconRight}>{rightIcon}</span>}
        {rightAction && <span className={styles.inputActionRight}>{rightAction}</span>}
      </div>
    </FormField>
  );
}

export default TextInput;
